import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface CancelStaleRunParams {
  runId: string;
  reason: string;
  performedBy: string;
}

export const useCancelStalePayrollRun = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ runId, reason, performedBy }: CancelStaleRunParams) => {
      const { data, error } = await supabase.rpc('cancel_stale_payroll_run', {
        p_run_id: runId,
        p_reason: reason,
        p_performed_by: performedBy,
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; deleted_payslips?: number };
      if (!result.success) {
        throw new Error(result.error || 'Failed to cancel payroll run');
      }

      return result;
    },
    onSuccess: (data) => {
      toast({
        title: 'Run Cancelled',
        description: `Stuck run cancelled. ${data.deleted_payslips || 0} orphaned payslip(s) cleaned up.`,
      });
      queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
      queryClient.invalidateQueries({ queryKey: ['payslips'] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Cancel Failed',
        description: error.message,
        variant: 'destructive',
      });
    },
  });
};
